import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse } from '../types';
import { throwIfAuthFailure } from './authError';

/**
 * Staff Settings API + hooks (supervision revamp Task 7).
 *
 * Backed by SQL-core (StaffProfile + ReviewerAssignment). The two GET reads are open to any
 * authenticated staff (roster/badges render for everyone); the mutations (PATCH/PUT) stay
 * admin-gated, returning 403 to non-admins.
 * The four hooks below are a Wave-3 contract — their exact names are imported by later tasks
 * (workload, productivity, badges, review-autogen), so DO NOT rename them:
 *   useStaffProfiles, useReviewerAssignments, useUpdateStaffProfile, useSetReviewerAssignment.
 *
 * Unlike the shared `fetchApi`, this module surfaces the HTTP status on failure (via ApiError) so
 * the Staff Settings page can distinguish a 403 (mutation denied — not an admin) and a 503 (SQL
 * core not configured) from a generic error and render a friendly state.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/** Error carrying the HTTP status so callers can branch on 403 / 503. */
export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchStaffApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const { headers, ...rest } = options ?? {};
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: { 'Content-Type': 'application/json', ...headers },
    ...rest,
  });

  throwIfAuthFailure(response);

  if (!response.ok) {
    // Try to lift the server's ApiResponse error message; fall back to the raw text.
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as ApiResponse<unknown>;
      if (body?.error) message = body.error;
    } catch {
      // non-JSON body; keep the default message
    }
    throw new ApiError(response.status, message);
  }

  const result: ApiResponse<T> = await response.json();
  if (!result.success) {
    throw new ApiError(response.status, result.error || 'Unknown error');
  }
  return result.data as T;
}

// ── Types ──────────────────────────────────────────────────────────────────

/**
 * The role tokens the settings UI offers.
 *
 * MUST stay in lockstep with `ValidRoles` in `api/Functions/StaffProfileFunctions.cs` — offering a
 * token the server does not know makes the save fail with a 400 — and with `roleDefs.ts`, which
 * gives each token its badge icon/colour.
 *
 * Supervisory roles are separate tokens from the doer roles on purpose: only the literal
 * `bookkeeper` token counts as a bookkeeper for the workload view.
 */
export const STAFF_ROLES = [
  { value: 'bookkeeper', label: 'Bookkeeper' },
  { value: 'payroll-admin', label: 'Payroll Admin' },
  { value: 'state-tax-admin', label: 'State Tax Admin' },
  { value: 'income-tax-preparer', label: 'Income Tax Preparer' },
  { value: 'business-services-coordinator', label: 'Business Services Coordinator' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'bookkeeping-supervisor', label: 'Bookkeeping Supervisor' },
  { value: 'payroll-supervisor', label: 'Payroll Supervisor' },
  { value: 'state-tax-supervisor', label: 'State Tax Supervisor' },
  { value: 'income-tax-reviewer', label: 'Income Tax Reviewer' },
] as const;

export type StaffRoleValue = (typeof STAFF_ROLES)[number]['value'];

export interface StaffProfile {
  staffId: string;
  fullName: string | null;
  systemUserId: string | null;
  jobTitle: string | null;
  roles: string[];
  otherActivitiesFactor: number;
  /**
   * Transaction capacity target (avg monthly transactions at 100% bookkeeping time) applied to this
   * staff member. Read-only while `capacityTargetEditable` is false — it is then the organisation-wide
   * value, because a per-staff target needs a new `core.StaffProfile` column that ships from the
   * PtcCore.Data package (not changeable from this repo).
   *
   * **NULL when `BOOKKEEPER_TXN_CAPACITY_TARGET` is unset**, which is the case in production. There
   * is no code default any more: the old one (1000) was a number nobody chose that silently became
   * the denominator of every capacity figure on the workload board.
   */
  transactionCapacityTarget: number | null;
  /** True once per-staff targets are persisted and therefore individually editable. */
  capacityTargetEditable: boolean;
  excludeFromSupervision: boolean;
  isReviewer: boolean;
  hasProfile: boolean;
}

export interface ReviewerAssignment {
  companyId: string;
  reviewerStaffId: string;
  reviewerName: string | null;
  assignedByStaffId: string | null;
  assignedAt: string;
}

export interface UpdateStaffProfileInput {
  staffId: string;
  roles?: string[];
  otherActivitiesFactor?: number;
  excludeFromSupervision?: boolean;
  isReviewer?: boolean;
}

export interface SetReviewerAssignmentInput {
  companyId: string;
  /** null clears the assignment for this client. */
  reviewerStaffId: string | null;
}

/**
 * Point MANY clients at ONE reviewer in a single ALL-OR-NOTHING call.
 *
 * This replaces the old N-sequential-PUTs fan-out, whose whole problem was that a batch could
 * half-land: some clients moved, some kept their old reviewer, and the resulting split looked
 * deliberate. The server validates everything first and then writes inside one transaction, so the
 * only two outcomes are "all of them" and "none of them".
 *
 * `reviewerStaffId: null` clears the assignment for every listed client.
 */
export interface BulkSetReviewerAssignmentsInput {
  companyIds: string[];
  reviewerStaffId: string | null;
}

export interface BulkSetReviewerAssignmentsResult {
  /** Clients newly pointed at the target reviewer (rows written). */
  assigned: number;
  /** Clients whose assignment was removed. */
  cleared: number;
  /** Clients already on the target and therefore left alone — this is what makes a repeat a no-op. */
  unchanged: number;
  /** Distinct client ids considered, after the server de-duplicated the list. */
  requested: number;
  reviewerStaffId: string | null;
  reviewerName: string | null;
}

/**
 * Move already-created OPEN reviews (pending | in-progress) to a new reviewer. Exactly one of
 * `fromStaffId` (bulk handoff) / `clientId` (per-client, account GUID) must be set. Completed and
 * cancelled reviews are never touched. Pass `dryRun` to get the count without writing (confirm dialog).
 */
export interface TransferOpenReviewsInput {
  toStaffId: string;
  toStaffName: string;
  fromStaffId?: string;
  clientId?: string;
  dryRun?: boolean;
}

export interface TransferOpenReviewsResult {
  transferred: number;
  dryRun: boolean;
  toStaffName: string | null;
}

// ── API ────────────────────────────────────────────────────────────────────

export const staffProfilesApi = {
  getStaffProfiles: (): Promise<StaffProfile[]> =>
    fetchStaffApi<StaffProfile[]>('/staff-profiles'),

  updateStaffProfile: (input: UpdateStaffProfileInput): Promise<StaffProfile> =>
    fetchStaffApi<StaffProfile>('/staff-profiles', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  getReviewerAssignments: (): Promise<ReviewerAssignment[]> =>
    fetchStaffApi<ReviewerAssignment[]>('/reviewer-assignments'),

  setReviewerAssignment: (input: SetReviewerAssignmentInput): Promise<ReviewerAssignment | null> =>
    fetchStaffApi<ReviewerAssignment | null>('/reviewer-assignments', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  bulkSetReviewerAssignments: (
    input: BulkSetReviewerAssignmentsInput,
  ): Promise<BulkSetReviewerAssignmentsResult> =>
    fetchStaffApi<BulkSetReviewerAssignmentsResult>('/reviewer-assignments/bulk', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  transferOpenReviews: (input: TransferOpenReviewsInput): Promise<TransferOpenReviewsResult> =>
    fetchStaffApi<TransferOpenReviewsResult>('/reviewer-assignments/transfer-open-reviews', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

// ── Hooks (Wave-3 contract — exact names matter) ─────────────────────────────

export const STAFF_PROFILES_QUERY_KEY = ['staffProfiles'] as const;
export const REVIEWER_ASSIGNMENTS_QUERY_KEY = ['reviewerAssignments'] as const;

export function useStaffProfiles() {
  return useQuery<StaffProfile[]>({
    queryKey: STAFF_PROFILES_QUERY_KEY,
    queryFn: () => staffProfilesApi.getStaffProfiles(),
    // Open to any authenticated staff; still don't retry an authz (403) or unavailable (503) failure.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 403 || error.status === 503)) && failureCount < 2,
  });
}

export function useReviewerAssignments() {
  return useQuery<ReviewerAssignment[]>({
    queryKey: REVIEWER_ASSIGNMENTS_QUERY_KEY,
    queryFn: () => staffProfilesApi.getReviewerAssignments(),
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 403 || error.status === 503)) && failureCount < 2,
  });
}

export function useUpdateStaffProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStaffProfileInput) => staffProfilesApi.updateStaffProfile(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAFF_PROFILES_QUERY_KEY });
    },
  });
}

export function useSetReviewerAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetReviewerAssignmentInput) => staffProfilesApi.setReviewerAssignment(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: REVIEWER_ASSIGNMENTS_QUERY_KEY });
    },
  });
}

/*
 * There is deliberately NO `useBulkSetReviewerAssignments` hook. The bulk assign is followed by an
 * optional per-client open-review pass, and the page invalidates the affected queries ONCE after
 * both phases finish; a mutation hook would invalidate between them and refetch the assignment list
 * mid-run. Call `staffProfilesApi.bulkSetReviewerAssignments` directly instead.
 */

export function useTransferOpenReviews() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferOpenReviewsInput) => staffProfilesApi.transferOpenReviews(input),
    onSuccess: (result) => {
      // A live transfer changes review ownership; refetch the reviews lists. (Dry runs write nothing,
      // but invalidating a fresh query is cheap and keeps the code path simple.)
      if (!result.dryRun) {
        void queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
      }
    },
  });
}
